import React, { useState, useMemo } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { flagFromCode } from "@/lib/countries";
import {
  ArrowLeft, ArrowRight, Check, CheckCircle2, Building2, Globe2, UserSquare2,
  Wallet, Target, Package, Upload, FileText, X, Sparkles, TrendingUp, Languages, Briefcase,
} from "lucide-react";

const CUSTOMER_TYPES = ["Εισαγωγείς", "Διανομείς", "Χονδρέμποροι", "Retail Chains", "Βιομηχανίες", "Private Label Συνεργασίες"];
const COUNTRIES = [
  { code: "DE", name: "Γερμανία" }, { code: "IT", name: "Ιταλία" }, { code: "FR", name: "Γαλλία" },
  { code: "ES", name: "Ισπανία" }, { code: "GB", name: "Ηνωμένο Βασίλειο" }, { code: "US", name: "ΗΠΑ" },
  { code: "CA", name: "Καναδάς" }, { code: "AE", name: "ΗΑΕ" }, { code: "SA", name: "Σαουδική Αραβία" },
  { code: "NL", name: "Ολλανδία" }, { code: "BE", name: "Βέλγιο" }, { code: "CH", name: "Ελβετία" },
  { code: "AT", name: "Αυστρία" }, { code: "SE", name: "Σουηδία" }, { code: "AU", name: "Αυστραλία" },
];
const EDU = ["Λύκειο", "Πτυχίο", "Μεταπτυχιακό", "Διδακτορικό", "Επαγγελματική κατάρτιση"];
const GOALS = ["Εύρεση Διανομέων", "Εύρεση Εισαγωγέων", "Δημιουργία Leads", "Κλείσιμο Συναντήσεων", "Ανάπτυξη Εξαγωγών", "Private Label Συνεργασίες", "Είσοδος σε Νέες Αγορές"];

const STEPS = [
  { num: 1, title: "Στοιχεία Επιχείρησης", subtitle: "Πείτε μας για την εταιρεία σας", icon: Building2 },
  { num: 2, title: "Χώρες-Στόχοι", subtitle: "Επιλέξτε αγορές για ανάπτυξη", icon: Globe2 },
  { num: 3, title: "Προφίλ Εκπροσώπου", subtitle: "Ο ιδανικός συνεργάτης σας", icon: UserSquare2 },
  { num: 4, title: "Αμοιβή & Διαθεσιμότητα", subtitle: "Πόσο και πότε", icon: Wallet },
  { num: 5, title: "Στόχοι Πωλήσεων", subtitle: "Τι θεωρείτε επιτυχία", icon: Target },
  { num: 6, title: "Προϊόντα & Υλικά", subtitle: "Ανεβάστε αρχεία παρουσίασης", icon: Package },
];

const empty = {
  company_name: "", website: "", industry: "", contact_person: "", position: "",
  phone: "", email: "", country: "Ελλάδα", employees: "", annual_revenue: "", current_export_markets: "",
  target_countries: [], customer_types: [],
  rep_education: "", rep_residence: "", rep_sales_experience: "", rep_industry_experience: "",
  rep_existing_network: "", rep_face_to_face: true, rep_languages: [],
  hourly_rate_min: 15, hourly_rate_max: 40, hours_per_week: 20, performance_bonus: "",
  goals: [], goals_description: "",
  product_description: "", competitive_advantages: "", uploaded_file_ids: [], uploaded_files: [],
};

export default function ClientOnboardingWizard() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [langInput, setLangInput] = useState("");

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleArr = (k, val) => setForm((f) => ({ ...f, [k]: f[k].includes(val) ? f[k].filter((x) => x !== val) : [...f[k], val] }));
  const progress = (step / STEPS.length) * 100;

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length));
  const prev = () => setStep((s) => Math.max(s - 1, 1));

  const canProceed = useMemo(() => {
    if (step === 1) return form.company_name && form.contact_person && form.email;
    if (step === 2) return form.target_countries.length > 0;
    return true;
  }, [step, form]);

  const submit = async () => {
    setSubmitting(true); setErr("");
    try {
      const payload = { ...form };
      delete payload.uploaded_files;
      const { data } = await api.post("/public/client-requests", payload);
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setErr(e.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); }
  };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      const fd = new FormData(); fd.append("file", file);
      try {
        const { data } = await api.post("/public/client-requests/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((f) => ({
          ...f,
          uploaded_file_ids: [...f.uploaded_file_ids, data.id],
          uploaded_files: [...f.uploaded_files, { id: data.id, name: data.filename, size: data.size }],
        }));
      } catch {}
    }
    e.target.value = "";
  };

  if (result) return <SuccessPage data={result} onReset={() => { setResult(null); setForm(empty); setStep(1); }} />;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-xl overflow-hidden" data-testid="client-wizard">
      {/* Progress bar */}
      <div className="px-6 md:px-10 pt-8 pb-6 border-b border-slate-100">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#2563EB]">Βήμα {step} από {STEPS.length}</div>
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-[#0F172A] mt-2 tracking-tight">{STEPS[step - 1].title}</h3>
            <p className="text-sm text-slate-500 mt-1">{STEPS[step - 1].subtitle}</p>
          </div>
          <div className="hidden md:flex w-14 h-14 rounded-2xl bg-[#2563EB]/10 text-[#2563EB] items-center justify-center">
            {React.createElement(STEPS[step - 1].icon, { className: "w-7 h-7" })}
          </div>
        </div>
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#2563EB] to-[#10B981] rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>
        <div className="mt-3 flex justify-between text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
          {STEPS.map((s) => (
            <span key={s.num} className={s.num <= step ? "text-[#2563EB]" : ""}>{s.num}</span>
          ))}
        </div>
      </div>

      {/* Step body */}
      <div className="px-6 md:px-10 py-8 min-h-[400px]">
        {step === 1 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Επωνυμία επιχείρησης" testid="cw-company" required value={form.company_name} onChange={(v) => update("company_name", v)} />
            <Field label="Ιστοσελίδα" testid="cw-website" value={form.website} onChange={(v) => update("website", v)} placeholder="https://" />
            <Field label="Κλάδος δραστηριότητας" testid="cw-industry" value={form.industry} onChange={(v) => update("industry", v)} />
            <Field label="Ονοματεπώνυμο υπευθύνου" testid="cw-contact" required value={form.contact_person} onChange={(v) => update("contact_person", v)} />
            <Field label="Θέση" testid="cw-position" value={form.position} onChange={(v) => update("position", v)} />
            <Field label="Τηλέφωνο" testid="cw-phone" value={form.phone} onChange={(v) => update("phone", v)} />
            <Field label="Email" testid="cw-email" type="email" required value={form.email} onChange={(v) => update("email", v)} />
            <Field label="Χώρα έδρας" testid="cw-country" value={form.country} onChange={(v) => update("country", v)} />
            <Select label="Αριθμός εργαζομένων" testid="cw-employees" value={form.employees} onChange={(v) => update("employees", v)} options={["1-10", "11-50", "51-200", "201-500", "500+"]} />
            <Select label="Ετήσιος κύκλος εργασιών" testid="cw-revenue" value={form.annual_revenue} onChange={(v) => update("annual_revenue", v)} options={["< €500K", "€500K - €2M", "€2M - €10M", "€10M - €50M", "€50M+"]} />
            <div className="md:col-span-2">
              <Field label="Υφιστάμενες εξαγωγές (χώρες, ποσοστό, κανάλια)" testid="cw-exports" value={form.current_export_markets} onChange={(v) => update("current_export_markets", v)} />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-700 mb-3">Επιλογή χωρών στόχων</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
                {COUNTRIES.map((c) => {
                  const active = form.target_countries.includes(c.name);
                  return (
                    <button
                      key={c.code}
                      type="button"
                      data-testid={`cw-country-${c.code}`}
                      onClick={() => toggleArr("target_countries", c.name)}
                      className={`group p-4 rounded-xl border-2 text-left transition-all hover:-translate-y-0.5 ${
                        active ? "border-[#2563EB] bg-[#2563EB]/5 shadow-md" : "border-slate-200 bg-white hover:border-slate-300"
                      }`}
                    >
                      <div className="text-3xl mb-1.5">{flagFromCode(c.code)}</div>
                      <div className="text-sm font-semibold text-[#0F172A]">{c.name}</div>
                      {active && <div className="mt-2 inline-flex items-center gap-1 text-[10px] text-[#10B981] font-bold uppercase tracking-wider"><Check className="w-3 h-3" /> Επιλεγμένη</div>}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-700 mb-3">Τύπος πελατών που αναζητάτε</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {CUSTOMER_TYPES.map((c) => {
                  const active = form.customer_types.includes(c);
                  return (
                    <button key={c} type="button" data-testid={`cw-ct-${c}`} onClick={() => toggleArr("customer_types", c)}
                      className={`px-4 py-3 rounded-lg border text-left text-sm font-medium transition-all ${active ? "border-[#10B981] bg-[#10B981]/10 text-[#0F172A]" : "border-slate-200 text-slate-700 hover:border-slate-300"}`}>
                      <span className="flex items-center gap-2">
                        <span className={`w-4 h-4 rounded border flex items-center justify-center ${active ? "border-[#10B981] bg-[#10B981]" : "border-slate-300"}`}>{active && <Check className="w-2.5 h-2.5 text-white" />}</span>
                        {c}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-5">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-700 mb-3">Επίπεδο σπουδών</div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                {EDU.map((e) => (
                  <button key={e} type="button" data-testid={`cw-edu-${e}`} onClick={() => update("rep_education", e)}
                    className={`px-3 py-3 rounded-lg border text-sm font-medium ${form.rep_education === e ? "border-[#2563EB] bg-[#2563EB]/5 text-[#2563EB]" : "border-slate-200 text-slate-700 hover:border-slate-300"}`}>
                    {e}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Μόνιμη κατοικία" testid="cw-rep-residence" value={form.rep_residence} onChange={(v) => update("rep_residence", v)} placeholder="π.χ. Γερμανία, Μόναχο" />
              <Select label="Εμπειρία πωλήσεων" testid="cw-rep-exp" value={form.rep_sales_experience} onChange={(v) => update("rep_sales_experience", v)} options={["1-3 χρόνια", "3-5 χρόνια", "5-10 χρόνια", "10+ χρόνια"]} />
              <Field label="Εμπειρία στον κλάδο" testid="cw-rep-ind" value={form.rep_industry_experience} onChange={(v) => update("rep_industry_experience", v)} />
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-700">Γλώσσες επικοινωνίας</label>
                <div className="mt-1.5 flex flex-wrap gap-1.5 p-2 border border-slate-200 rounded-lg min-h-[44px]">
                  {form.rep_languages.map((l) => (
                    <span key={l} className="inline-flex items-center gap-1 px-2 py-1 bg-[#2563EB]/10 text-[#2563EB] rounded text-xs font-semibold">
                      <Languages className="w-3 h-3" /> {l}
                      <button type="button" onClick={() => toggleArr("rep_languages", l)} className="hover:text-red-600"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                  <input
                    data-testid="cw-rep-lang-input"
                    value={langInput}
                    onChange={(e) => setLangInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && langInput.trim()) { e.preventDefault(); toggleArr("rep_languages", langInput.trim()); setLangInput(""); } }}
                    placeholder="Πληκτρολογήστε & Enter"
                    className="flex-1 min-w-[120px] px-2 py-1 text-sm outline-none"
                  />
                </div>
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-700">Υπάρχον δίκτυο επαφών</label>
              <textarea data-testid="cw-rep-network" value={form.rep_existing_network} onChange={(e) => update("rep_existing_network", e.target.value)} rows={3} placeholder="π.χ. επαφές με buyers σε αλυσίδες σούπερ μάρκετ, distributors σε συγκεκριμένες περιοχές..." className="mt-1.5 w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm" />
            </div>
            <label className="flex items-center gap-3 p-4 bg-slate-50 border border-slate-200 rounded-lg cursor-pointer">
              <input type="checkbox" data-testid="cw-rep-face" checked={form.rep_face_to_face} onChange={(e) => update("rep_face_to_face", e.target.checked)} />
              <span className="text-sm text-[#0F172A] font-medium">Επιθυμώ ο εκπρόσωπος να πραγματοποιεί συναντήσεις από κοντά (face-to-face)</span>
            </label>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-6 max-w-2xl">
            <div className="grid grid-cols-2 gap-5">
              <NumField label="Από (€ / ώρα)" testid="cw-hr-min" value={form.hourly_rate_min} onChange={(v) => update("hourly_rate_min", parseFloat(v) || 0)} />
              <NumField label="Έως (€ / ώρα)" testid="cw-hr-max" value={form.hourly_rate_max} onChange={(v) => update("hourly_rate_max", parseFloat(v) || 0)} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-700">Ώρες ανά εβδομάδα: <span className="text-[#2563EB] font-bold">{form.hours_per_week}h</span></label>
              <input type="range" min="5" max="40" step="5" data-testid="cw-hpw" value={form.hours_per_week} onChange={(e) => update("hours_per_week", parseInt(e.target.value))} className="mt-3 w-full accent-[#2563EB]" />
              <div className="flex justify-between text-[10px] text-slate-400 mt-1"><span>5h</span><span>10h</span><span>20h</span><span>30h</span><span>40h</span></div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-700">Bonus απόδοσης</label>
              <Select inline testid="cw-bonus" value={form.performance_bonus} onChange={(v) => update("performance_bonus", v)} options={["Όχι", "% επί τζίρου", "Σταθερό bonus ανά συμφωνία", "Συνδυασμός"]} />
            </div>
          </div>
        )}

        {step === 5 && (
          <div className="space-y-5">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-700 mb-3">Επιλέξτε τους στόχους σας</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                {GOALS.map((g) => {
                  const active = form.goals.includes(g);
                  return (
                    <button key={g} type="button" data-testid={`cw-goal-${g}`} onClick={() => toggleArr("goals", g)}
                      className={`p-4 rounded-xl border-2 text-left text-sm font-semibold transition-all hover:-translate-y-0.5 ${active ? "border-[#10B981] bg-[#10B981]/5 text-[#0F172A]" : "border-slate-200 text-slate-700 hover:border-slate-300"}`}>
                      <span className="flex items-center gap-2">
                        <span className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${active ? "border-[#10B981] bg-[#10B981]" : "border-slate-300"}`}>{active && <Check className="w-3 h-3 text-white" />}</span>
                        {g}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-700">Περιγράψτε αναλυτικά τους στόχους σας</label>
              <textarea data-testid="cw-goals-desc" value={form.goals_description} onChange={(e) => update("goals_description", e.target.value)} rows={5} placeholder="π.χ. Θέλουμε να αυξήσουμε τις εξαγωγές στη Γερμανία κατά 40% σε 12 μήνες, εστιάζοντας σε premium retail channels..." className="mt-1.5 w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm" />
            </div>
          </div>
        )}

        {step === 6 && (
          <div className="space-y-5">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-700">Περιγραφή προϊόντων / υπηρεσιών</label>
              <textarea data-testid="cw-product-desc" value={form.product_description} onChange={(e) => update("product_description", e.target.value)} rows={3} className="mt-1.5 w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-700">Ανταγωνιστικά πλεονεκτήματα</label>
              <textarea data-testid="cw-advantages" value={form.competitive_advantages} onChange={(e) => update("competitive_advantages", e.target.value)} rows={3} placeholder="π.χ. ISO certified, organic, family business 40+ years, exclusive recipe..." className="mt-1.5 w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-700">
                Ανεβάστε υλικό (κατάλογος, πιστοποιήσεις, εταιρική παρουσίαση, τιμοκατάλογος, φωτογραφίες)
              </label>
              <label className="mt-2 flex flex-col items-center justify-center gap-2 p-8 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-[#2563EB] hover:bg-[#2563EB]/5 transition-colors">
                <Upload className="w-8 h-8 text-slate-400" />
                <div className="text-sm font-semibold text-slate-700">Σύρετε αρχεία ή πατήστε για επιλογή</div>
                <div className="text-xs text-slate-500">PDF, DOCX, JPG, PNG · έως 25MB ανά αρχείο</div>
                <input type="file" multiple onChange={onUpload} className="hidden" data-testid="cw-upload" accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp" />
              </label>
              {form.uploaded_files.length > 0 && (
                <div className="mt-3 space-y-1.5">
                  {form.uploaded_files.map((f) => (
                    <div key={f.id} className="flex items-center gap-2 p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg text-sm">
                      <FileText className="w-4 h-4 text-emerald-700 flex-shrink-0" />
                      <span className="flex-1 truncate text-emerald-900 font-medium">{f.name}</span>
                      <span className="text-xs text-emerald-700">{(f.size / 1024).toFixed(0)} KB</span>
                      <button type="button" onClick={() => setForm((s) => ({ ...s, uploaded_file_ids: s.uploaded_file_ids.filter((x) => x !== f.id), uploaded_files: s.uploaded_files.filter((x) => x.id !== f.id) }))} className="text-emerald-700 hover:text-red-600"><X className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {err && <div className="mx-6 md:mx-10 mb-4 text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">{err}</div>}

      {/* Sticky nav */}
      <div className="px-6 md:px-10 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between sticky bottom-0">
        <button onClick={prev} disabled={step === 1} data-testid="cw-prev" className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-slate-600 disabled:opacity-30 hover:text-[#0F172A]">
          <ArrowLeft className="w-4 h-4" /> Προηγούμενο
        </button>
        {step < STEPS.length ? (
          <button onClick={next} disabled={!canProceed} data-testid="cw-next" className="inline-flex items-center gap-2 px-6 py-3 bg-[#2563EB] text-white rounded-lg font-bold disabled:opacity-50 hover:bg-[#1d4ed8] shadow-lg shadow-[#2563EB]/20">
            Επόμενο <ArrowRight className="w-4 h-4" />
          </button>
        ) : (
          <button onClick={submit} disabled={submitting} data-testid="cw-submit" className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#2563EB] to-[#10B981] text-white rounded-lg font-bold disabled:opacity-50 shadow-lg shadow-[#2563EB]/30">
            <Sparkles className="w-4 h-4" />
            {submitting ? "Αποστολή..." : "Βρες μου τον κατάλληλο εκπρόσωπο πωλήσεων"}
          </button>
        )}
      </div>
    </div>
  );
}

function SuccessPage({ data, onReset }) {
  const r = data.recommendation || {};
  return (
    <div className="space-y-6" data-testid="cw-success">
      {/* AI Recommendation */}
      <div className="bg-gradient-to-br from-[#0F172A] to-[#1E3A8A] rounded-2xl p-8 md:p-12 text-white shadow-2xl overflow-hidden relative">
        <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[#10B981]/20 blur-3xl" />
        <div className="relative z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-xs uppercase tracking-[0.2em] font-bold mb-4">
            <Sparkles className="w-3 h-3" /> AI Export Matching Analysis
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Η ανάλυσή μας για εσάς</h2>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6">
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-white/70 mb-2">Estimated Success Score</div>
              <div className="font-heading text-6xl font-bold leading-none">{r.score}<span className="text-2xl text-white/60">/100</span></div>
              <div className="mt-3 h-2 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#10B981] to-emerald-300 rounded-full" style={{ width: `${r.score}%` }} />
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6 space-y-3">
              <RecLine icon={Globe2} label="Προτεινόμενες χώρες" value={(r.recommended_countries || []).join(", ")} />
              <RecLine icon={Languages} label="Γλώσσες" value={(r.recommended_languages || []).join(", ")} />
              <RecLine icon={Briefcase} label="Κλάδοι" value={(r.recommended_industries || []).join(", ")} />
            </div>
          </div>

          <div className="mt-6 bg-white/5 border border-white/10 rounded-xl p-5">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-white/70 mb-2">Προφίλ συνεργάτη</div>
            <div className="text-base">{r.rep_profile_summary}</div>
          </div>
        </div>
      </div>

      {/* Confirmation */}
      <div className="bg-white border border-slate-200 rounded-2xl p-8 md:p-10 text-center">
        <div className="w-16 h-16 rounded-full bg-[#10B981] text-white flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-8 h-8" />
        </div>
        <h3 className="font-heading text-2xl md:text-3xl font-bold text-[#0F172A]">Το αίτημά σας υποβλήθηκε επιτυχώς!</h3>
        <p className="mt-3 text-slate-600 max-w-2xl mx-auto">
          Η ομάδα μας θα εξετάσει τις απαιτήσεις σας και θα σας προτείνει τους πιο κατάλληλους διεθνείς εκπροσώπους πωλήσεων.
        </p>
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-2xl mx-auto">
          <Check2 text="Στοιχεία εταιρείας" />
          <Check2 text="Χώρες στόχοι" />
          <Check2 text="Προφίλ εκπροσώπου" />
          <Check2 text="Υλικό προϊόντων" />
        </div>
        <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-full text-sm text-amber-800 font-semibold">
          ⏱ Εκτιμώμενος χρόνος matching: {r.expected_match_days || "24-48 ώρες"}
        </div>
        <div className="mt-6 flex flex-col sm:flex-row justify-center gap-3">
          <a href="/register" className="px-6 py-3 bg-[#2563EB] text-white rounded-lg font-bold hover:bg-[#1d4ed8]" data-testid="cw-success-register">
            Δημιουργία λογαριασμού στην πλατφόρμα
          </a>
          <button onClick={onReset} className="px-6 py-3 border border-slate-200 text-slate-700 rounded-lg font-semibold hover:bg-slate-50" data-testid="cw-success-new">
            Νέα αίτηση
          </button>
        </div>
      </div>
    </div>
  );
}

function RecLine({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2.5">
      <Icon className="w-4 h-4 text-emerald-300 mt-0.5 flex-shrink-0" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.2em] text-white/60 font-bold">{label}</div>
        <div className="text-sm font-medium truncate">{value || "—"}</div>
      </div>
    </div>
  );
}

function Check2({ text }) {
  return (
    <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-left">
      <Check className="w-4 h-4 text-emerald-700 flex-shrink-0" />
      <span className="text-sm font-semibold text-emerald-900">{text}</span>
    </div>
  );
}

function Field({ label, testid, value, onChange, type = "text", required, placeholder }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-700">{label}{required && <span className="text-[#2563EB]">*</span>}</label>
      <input data-testid={testid} type={type} required={required} placeholder={placeholder} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1.5 w-full px-3.5 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]/30 focus:border-[#2563EB] transition-all" />
    </div>
  );
}

function NumField({ label, testid, value, onChange }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-700">{label}</label>
      <input data-testid={testid} type="number" min="0" value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1.5 w-full px-3.5 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]/30" />
    </div>
  );
}

function Select({ label, testid, value, onChange, options, inline }) {
  return (
    <div className={inline ? "mt-2" : ""}>
      {label && !inline && <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-700">{label}</label>}
      <select data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${inline ? "" : "mt-1.5"} w-full px-3.5 py-2.5 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#2563EB]/30`}>
        <option value="">Επιλέξτε...</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
